import { useEffect, useState } from 'react';

/**
 * Devuelve la coordenada Y —en píxeles del documento del micrositio— que está
 * verticalmente centrada en lo que el usuario REALMENTE está viendo. `null`
 * mientras no haya dato.
 *
 * PARA QUÉ SIRVE. Los controles flotantes del micrositio (hoy las flechas
 * prev/next de `AnimalPage`) son `position: fixed`, y eso deja de significar
 * algo en cuanto el sitio que nos incrusta agranda el iframe hasta que entra
 * todo el contenido — ver `useAvisarAltoAlPadre`. Un `fixed` se fija al
 * viewport DEL IFRAME, y ese viewport pasa a medir lo mismo que el documento
 * entero: el control queda al 50% de toda la página y se va con el scroll.
 *
 * Desde adentro del iframe NO hay forma de saber dónde scrolleó el padre: es
 * otro dominio. Así que se lo pedimos. El snippet de incrustación —lo genera
 * `Herramientas/Micrositio.jsx`— postea `{ type: 'puja-viewport', centro }` en
 * cada scroll y cada resize.
 *
 * POR QUÉ EL PADRE MANDA EL CENTRO YA CALCULADO, y no `scrollY` + alto: el
 * padre es el único que sabe en qué parte de SU página arranca el iframe,
 * cuánto quedó por encima del borde superior y cuánto se ve. Mandando el número
 * final, acá no hay que reconstruir nada ni adivinar offsets, y los casos de
 * borde —arriba de todo, abajo de todo, iframe entrando a medias— salen bien
 * solos en vez de necesitar tres condiciones.
 *
 * DEGRADA SOLO, A PROPÓSITO. Si el productor todavía tiene pegado el snippet
 * viejo, el mensaje no llega nunca, esto devuelve `null` y quien lo llama se
 * queda exactamente con el comportamiento que tenía. Ningún sitio se rompe por
 * no haber actualizado el código de incrustación.
 */
export function useCentroVisibleDelPadre() {
  const [centro, setCentro] = useState(null);

  useEffect(() => {
    // Sin padre no hay nada que escuchar: standalone el `fixed` anda perfecto.
    if (typeof window === 'undefined' || window.parent === window) return undefined;

    const onMensaje = (e) => {
      // DE QUIEN VIENE, igual que del lado del padre. `message` en window
      // escucha a TODA la pagina, y esta pagina hospeda iframes de terceros:
      // el player de streaming de `EnVivoPage`. Un reproductor, una extension
      // del navegador o cualquier embed futuro es un emisor co-residente; que
      // postee un `puja-viewport` con un centro absurdo manda las flechas a
      // `top: 99999px`, fuera del documento. Solo el padre tiene voz aca.
      if (e.source !== window.parent) return;

      const d = e.data;
      if (!d || d.type !== 'puja-viewport') return;
      // Viene de otro origen y termina en un `style`: se valida que sea un
      // número de verdad antes de usarlo. `Number.isFinite` y no un truthy
      // pelado, que dejaría pasar NaN e Infinity.
      if (typeof d.centro !== 'number' || !Number.isFinite(d.centro)) return;
      // Redondeado: el padre puede mandar decimales en pantallas con zoom, y
      // no tiene sentido re-renderizar por un cuarto de píxel.
      setCentro((previo) => {
        const nuevo = Math.round(d.centro);
        return nuevo === previo ? previo : nuevo;
      });
    };

    window.addEventListener('message', onMensaje);

    // Y AVISAMOS QUE ESTAMOS, despues de montar el listener para no perdernos
    // la respuesta. Sin esto no llega nada hasta el proximo scroll del usuario.
    //
    // Este hook se monta de nuevo en CADA cambio de animal: `AnimalPage` pone
    // `loading` en true al cambiar el slug y su early return desmonta todo lo
    // que esta abajo, `FloatingNav` incluido. Vuelve sin dato.
    //
    // Se REUSA `micrositio-ready` —el unico mensaje ante el cual el snippet
    // limpia su ultimo centro y reenvia— y no se inventa uno nuevo, porque el
    // snippet queda pegado en el sitio del productor y agregarle un tipo
    // obligaria a que lo re-peguen. Esta linea, en cambio, viaja en nuestro
    // bundle y se puede corregir siempre.
    //
    // El `syncDeepLink()` que esto vuelve a disparar del lado del padre es
    // inofensivo: `RematePage` lo absorbe con `deepLinkConsumed`.
    window.parent.postMessage({ type: 'micrositio-ready' }, '*');

    return () => window.removeEventListener('message', onMensaje);
  }, []);

  return centro;
}
